import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useI18n } from "@/i18n/I18nProvider";
import { colors } from "@/utils/const";

/*
 * The bar sits directly under the header, whose height depends on the viewport
 * and — because the Arabic menu is wider — on the locale too.
 */
function useHeaderHeight(isArabic) {
  const is1170to1200 = useMediaQuery("(min-width:1170px) and (max-width:1200px)");
  const is1230to1306 = useMediaQuery("(min-width:1230px) and (max-width:1306px)");
  const isAbove1306 = useMediaQuery("(min-width:1306px)");
  const isAbove900 = useMediaQuery("(min-width:900px)");

  if (isAbove1306 || (isArabic && (is1230to1306 || is1170to1200))) return 130;
  if (isAbove900) return 177;

  return 55;
}

export default function ScrollProgressBar({
  height = 5,
  color = colors?.orange,
  zIndex = 1000,
  contentSelector = "#main-content",
}) {
  const { locale } = useI18n();
  const isArabic = locale === "ar";

  const headerHeight = useHeaderHeight(isArabic);

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;

      const content = document.querySelector(contentSelector);

      if (!content) {
        setProgress(0);
        return;
      }

      const contentTop = content.getBoundingClientRect().top + window.scrollY;
      const scrollable = content.offsetHeight - window.innerHeight;
      const scrolled = window.scrollY - contentTop;

      const next =
        scrollable > 0
          ? Math.min(Math.max((scrolled / scrollable) * 100, 0), 100)
          : 0;

      // Scroll fires far more often than the bar can visibly change.
      setProgress((prev) => (prev === next ? prev : next));
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [contentSelector]);

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        top: headerHeight,
        left: isArabic ? "unset" : 0,
        right: isArabic ? 0 : "unset",
        width: `${progress}%`,
        height: `${height}px`,
        background: color,
        zIndex,
        transition: "width 0.2s",
        borderRadius: isArabic ? "3px 0 0 3px" : "0 3px 3px 0",
      }}
    />
  );
}
